import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { Clock, Pencil, Save, XCircle, AlertTriangle, Search, Calendar, Building2, Users2, TrendingUp, CheckCircle2, Download, BarChart3, Sparkles, Loader2, RefreshCw } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { bolehPakaiAI } from "../lib/izinAI";
import { toast } from "sonner";
import { DatePicker, MonthPicker } from "@/components/ui/date-picker";
import { hariIniISO } from "../lib/tanggal";

/** Aturan urutan tabel absensi, dipisah supaya baris absensi sungguhan dan
 *  baris OFF sintetis diurutkan dengan aturan yang SAMA. Kalau disalin jadi dua,
 *  keduanya berpisah jalan begitu salah satunya diubah dan OFF akan menggumpal
 *  di ujung tabel alih-alih duduk di tanggalnya. */
function pembanding(sortBy) {
  const tglTurun = (a, b) => (b.date || "").localeCompare(a.date || "");
  if (sortBy === "branch") return (a, b) => (a.branch || "").localeCompare(b.branch || "") || tglTurun(a, b);
  if (sortBy === "divisi") return (a, b) => (a.divisi || "").localeCompare(b.divisi || "") || tglTurun(a, b);
  if (sortBy === "nama") return (a, b) => (a.employee_nama || "").localeCompare(b.employee_nama || "") || tglTurun(a, b);
  return tglTurun;
}

export default function Attendance() {
  const { user, can } = useAuth();
  const canEditClock = user?.role === "superadmin" || user?.role === "admin" || can?.("attendance_edit", "update");
  const [items, setItems] = useState([]);
  const [emps, setEmps] = useState([]);
  const [cutiItems, setCutiItems] = useState([]);
  const [q, setQ] = useState("");
  const [empFilter, setEmpFilter] = useState("");
  const [branchFilter, setBranchFilter] = useState("");
  const [divisiFilter, setDivisiFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sortBy, setSortBy] = useState("date"); // date | branch | divisi | nama
  const [compareMonthA, setCompareMonthA] = useState("");
  const [compareMonthB, setCompareMonthB] = useState("");
  const [editModal, setEditModal] = useState(null);
  const [editForm, setEditForm] = useState({ clock_in: "", clock_out: "", note: "" });
  const [editSaving, setEditSaving] = useState(false);
  // Siapa yang OFF (brief owner 29 Agu poin 5; diperluas 2 Sep 2026). Orang
  // yang libur TIDAK punya baris absensi sama sekali, jadi tanpa ini "tidak ada
  // datanya" tidak bisa dibedakan dari "tidak absen padahal masuk".
  const [off, setOff] = useState(null);

  const load = () => api.get("/attendance").then(r => setItems(r.data));
  useEffect(() => {
    load();
    api.get("/employees").then(r => setEmps(r.data || [])).catch(() => {});
    api.get("/cuti").then(r => setCutiItems(r.data || [])).catch(() => {});
  }, []);

  const fmt = (iso) => iso ? new Date(iso).toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" }) : "-";
  const isoToHHMM = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      const wib = new Date(d.getTime() + (7 * 60 - d.getTimezoneOffset()) * 60000);
      return `${String(wib.getUTCHours()).padStart(2, "0")}:${String(wib.getUTCMinutes()).padStart(2, "0")}`;
    } catch { return ""; }
  };

  // Enrich attendance with employee divisi (join)
  const empById = useMemo(() => Object.fromEntries(emps.map(e => [e.id, e])), [emps]);
  const enriched = useMemo(() => items.map(a => ({
    ...a,
    divisi: empById[a.employee_id]?.divisi || "",
  })), [items, empById]);

  const branches = useMemo(() => [...new Set(enriched.map(a => a.branch).filter(Boolean))].sort(), [enriched]);
  const divisis = useMemo(() => [...new Set(enriched.map(a => a.divisi).filter(Boolean))].sort(), [enriched]);

  // Filter
  const filtered = useMemo(() => {
    let r = enriched;
    if (empFilter) r = r.filter(a => a.employee_id === empFilter);
    if (branchFilter) r = r.filter(a => a.branch === branchFilter);
    if (divisiFilter) r = r.filter(a => a.divisi === divisiFilter);
    if (dateFrom) r = r.filter(a => a.date >= dateFrom);
    if (dateTo) r = r.filter(a => a.date <= dateTo);
    if (q) {
      const s = q.toLowerCase();
      r = r.filter(a => (a.employee_nama || "").toLowerCase().includes(s)
                    || (a.branch || "").toLowerCase().includes(s)
                    || (a.divisi || "").toLowerCase().includes(s));
    }
    return [...r].sort(pembanding(sortBy));
  }, [enriched, empFilter, branchFilter, divisiFilter, dateFrom, dateTo, q, sortBy]);

  // Rentang untuk membaca OFF. Tabel menyaring dengan PERIODE (dari–sampai) dan
  // ujung yang DIKOSONGKAN berarti "tak terbatas" — jadi ujung kosong di sini
  // diambil dari tanggal yang benar-benar tampil di tabel. Kalau tidak, memilih
  // hanya "dari" membuat OFF menyusut ke satu hari sementara tabelnya
  // menampilkan berbulan-bulan, dan orang mengira memang tidak ada yang libur.
  // Ujung rentang diturunkan dari `enriched` (SELURUH absensi), BUKAN `filtered`.
  // Kalau memakai `filtered`, memilih satu karyawan di dropdown akan mempersempit
  // rentangnya ke tanggal absensi orang itu saja — dan hari-hari OFF-nya (yang
  // justru TIDAK punya baris absensi) ikut lenyap. Menyaring untuk melihat satu
  // orang malah menyembunyikan datanya sendiri.
  const rentangData = useMemo(() => {
    const t = enriched.map(a => a.date).filter(Boolean);
    if (!t.length) return null;
    return { min: t.reduce((a, b) => (a < b ? a : b)), max: t.reduce((a, b) => (a > b ? a : b)) };
  }, [enriched]);

  const offRange = useMemo(() => {
    const hariIni = hariIniISO();
    const p = (n) => String(n).padStart(2, "0");
    // Dirakit dari komponen LOKAL di semua tempat — `toISOString()` itu UTC dan
    // memberi tanggal kemarin bagi pengguna WIB sebelum pukul 07:00.
    const keIso = (d) => `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
    const akhirBulan = (iso) => {
      const d = new Date(`${iso}T00:00:00`);
      return keIso(new Date(d.getFullYear(), d.getMonth() + 1, 0));
    };

    const dari = dateFrom || rentangData?.min || hariIni;
    // Ujung atas TIDAK PERNAH lebih kecil dari ujung bawah. Versi sebelumnya
    // menukar keduanya saat `dari > sampai`, dan itu MEMBALIK maksud pengguna:
    // mengisi "dari 1 Okt" (memeriksa jadwal off bulan depan) menghasilkan
    // permintaan 4 Sep–1 Okt — menampilkan justru periode yang dikecualikan dan
    // menyembunyikan periode yang diminta.
    let sampai = dateTo;
    if (!sampai) {
      // Belum dikunci pengguna: kalau ujung bawahnya di masa depan, tampilkan
      // sebulan penuh dari sana (itu maksudnya "lihat jadwal bulan depan");
      // selain itu cukup sampai hari ini.
      sampai = dari > hariIni ? akhirBulan(dari) : hariIni;
    }
    if (sampai < dari) sampai = dari;

    // Backend menolak rentang > 366 hari. Yang dipendekkan ujung LAMA, supaya
    // permintaannya tidak gagal total dan panelnya kosong tanpa penjelasan.
    const MAKS = 366;
    if ((new Date(`${sampai}T00:00:00`) - new Date(`${dari}T00:00:00`)) / 86400000 > MAKS) {
      const d = new Date(`${sampai}T00:00:00`);
      d.setDate(d.getDate() - MAKS);
      return { dari: keIso(d), sampai };
    }
    return { dari, sampai };
  }, [dateFrom, dateTo, rentangData]);

  // Dependensinya nilai string, bukan identitas objek — objek baru tiap render
  // akan memicu permintaan berulang tanpa ada yang berubah.
  useEffect(() => {
    api.get("/attendance/off-rentang", { params: { dari: offRange.dari, sampai: offRange.sampai } })
      .then(r => setOff(r.data)).catch(() => setOff(null));
  }, [offRange.dari, offRange.sampai]);

  // Baris sintetis "OFF" (brief owner 2 Sep 2026). Karyawan yang libur tidak
  // punya dokumen absensi, jadi satu-satunya cara memunculkan namanya di tabel
  // harian adalah membuatkan barisnya di sini. Ditandai `_off` supaya tidak
  // pernah tertukar dengan absensi sungguhan — tidak bisa diedit jamnya, dan
  // tidak ikut perhitungan telat/lembur mana pun.
  const barisOff = useMemo(() => {
    let r = (off?.baris || []).map(b => {
      const e = empById[b.employee_id] || {};
      return {
        id: `off-${b.date}-${b.employee_id}`,
        _off: true,
        date: b.date,
        employee_id: b.employee_id,
        employee_nama: e.nama || b.employee_id,
        branch: e.branch || "",
        divisi: e.divisi || "",
        keterangan: b.keterangan || "OFF",
      };
    });
    // Saringan yang SAMA PERSIS dengan tabel — termasuk TANGGAL. Rentang yang
    // diminta ke server sengaja boleh lebih lebar dari periode yang dipilih
    // (mis. saat salah satu ujung dikosongkan), jadi tanpa dua baris tanggal di
    // bawah ini baris OFF bisa tergambar untuk tanggal yang justru dikecualikan
    // pengguna — persis kebalikan dari yang dia minta.
    if (dateFrom) r = r.filter(a => a.date >= dateFrom);
    if (dateTo) r = r.filter(a => a.date <= dateTo);
    if (empFilter) r = r.filter(a => a.employee_id === empFilter);
    if (branchFilter) r = r.filter(a => a.branch === branchFilter);
    if (divisiFilter) r = r.filter(a => a.divisi === divisiFilter);
    if (q) {
      const s = q.toLowerCase();
      r = r.filter(a => (a.employee_nama || "").toLowerCase().includes(s)
                    || (a.branch || "").toLowerCase().includes(s)
                    || (a.divisi || "").toLowerCase().includes(s));
    }
    return r;
  }, [off, empById, empFilter, branchFilter, divisiFilter, q, dateFrom, dateTo]);

  // Periode yang BENAR-BENAR tergambar — bukan rentang yang diminta ke server.
  // Keduanya boleh berbeda (rentang permintaan sengaja bisa lebih lebar), dan
  // judul panel harus menyebut yang dilihat pengguna, bukan yang diambil mesin.
  const rentangTampil = useMemo(() => {
    const dari = dateFrom || offRange.dari;
    const sampai = dateTo || offRange.sampai;
    return { dari, sampai: sampai < dari ? dari : sampai };
  }, [dateFrom, dateTo, offRange.dari, offRange.sampai]);

  // Libur umum juga disaring ke periode yang tergambar — sumbernya sama
  // (rentang permintaan), jadi tanpa ini judul panel bisa menyebut libur
  // nasional yang berada di luar periode yang dipilih pengguna.
  const liburUmumTampil = useMemo(() => (off?.libur_umum || []).filter(
    l => (!dateFrom || l.date >= dateFrom) && (!dateTo || l.date <= dateTo)),
    [off, dateFrom, dateTo]);

  // Yang benar-benar digambar: absensi + OFF, diurutkan bersama dengan
  // pembanding yang sama persis supaya OFF tidak menggumpal di ujung tabel.
  const barisTampil = useMemo(
    () => [...filtered, ...barisOff].sort(pembanding(sortBy)),
    [filtered, barisOff, sortBy]);

  // Month vs Month comparison
  const monthStats = (yyyymm) => {
    if (!yyyymm) return null;
    const monthItems = enriched.filter(a => (a.date || "").startsWith(yyyymm));
    // Rata-rata stamina hanya dari hari yang dijawab — hari kosong tidak dianggap 0.
    const staminaVals = monthItems.map(a => a.stamina_pct).filter(v => v != null);
    return {
      total: monthItems.length,
      late: monthItems.filter(a => (a.late_minutes || 0) > 0).length,
      totalLateMinutes: monthItems.reduce((s, a) => s + (a.late_minutes || 0), 0),
      totalOvertime: monthItems.reduce((s, a) => s + (a.overtime_minutes || 0), 0),
      totalEarlyLeave: monthItems.reduce((s, a) => s + (a.early_leave_minutes || 0), 0),
      avgStamina: staminaVals.length
        ? Math.round(staminaVals.reduce((s, v) => s + v, 0) / staminaVals.length * 10) / 10
        : null,
      uniqueEmps: new Set(monthItems.map(a => a.employee_id)).size,
    };
  };
  const statsA = monthStats(compareMonthA);
  const statsB = monthStats(compareMonthB);

  // Cuti counters (respect date/employee filter loosely)
  const cutiSummary = useMemo(() => {
    let r = cutiItems;
    if (empFilter) r = r.filter(c => c.employee_id === empFilter);
    if (dateFrom) r = r.filter(c => (c.tanggal_mulai || "") >= dateFrom);
    if (dateTo) r = r.filter(c => (c.tanggal_mulai || "") <= dateTo);
    return {
      approved: r.filter(c => c.status === "approved").length,
      pending: r.filter(c => c.status === "pending_manager" || c.status === "pending_hr" || c.status === "pending").length,
      rejected: r.filter(c => c.status === "rejected").length,
      total: r.length,
    };
  }, [cutiItems, empFilter, dateFrom, dateTo]);

  const openEditClock = async (att) => {
    setEditModal({ att });
    setEditForm({ clock_in: isoToHHMM(att.clock_in), clock_out: isoToHHMM(att.clock_out), note: "" });
  };
  const saveEditClock = async () => {
    if (!editForm.note.trim()) { toast.error("Catatan alasan wajib"); return; }
    setEditSaving(true);
    try {
      const { data } = await api.patch(`/attendance/${editModal.att.id}/edit-clock`, editForm);
      toast.success(data.changes?.length ? `Tersimpan (${data.changes.length} perubahan)` : "Tidak ada perubahan");
      setEditModal(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
    finally { setEditSaving(false); }
  };

  const clearFilters = () => {
    setQ(""); setEmpFilter(""); setBranchFilter(""); setDivisiFilter("");
    setDateFrom(""); setDateTo(""); setSortBy("date");
  };

  const exportExcel = async () => {
    try {
      const resp = await api.post("/attendance/export-excel", {
        from_date: dateFrom || null, to_date: dateTo || null,
        employee_id: empFilter || null, branch: branchFilter || null, divisi: divisiFilter || null,
      }, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url; a.download = `rekap_absensi_${dateFrom || "all"}_${dateTo || "all"}.xlsx`; a.click();
      URL.revokeObjectURL(url);
      toast.success(`Excel diunduh (${filtered.length} baris terfilter)`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal export"); }
  };

  // Chart data for comparison: hadir per divisi/cabang
  const chartData = useMemo(() => {
    const source = compareMonthA || compareMonthB
      ? enriched.filter(a => (a.date || "").startsWith(compareMonthA) || (a.date || "").startsWith(compareMonthB))
      : filtered;
    const byBranch = {}; const byDivisi = {};
    source.forEach(a => {
      const b = a.branch || "-"; const d = a.divisi || "-";
      byBranch[b] = (byBranch[b] || 0) + 1;
      byDivisi[d] = (byDivisi[d] || 0) + 1;
    });
    return {
      byBranch: Object.entries(byBranch).sort((a, b) => b[1] - a[1]).slice(0, 8),
      byDivisi: Object.entries(byDivisi).sort((a, b) => b[1] - a[1]).slice(0, 8),
    };
  }, [enriched, filtered, compareMonthA, compareMonthB]);

  const maxBranch = Math.max(...chartData.byBranch.map(([, v]) => v), 1);
  const maxDivisi = Math.max(...chartData.byDivisi.map(([, v]) => v), 1);

  return (
    <div className="p-4 md:p-8 max-w-[1400px]" data-testid="attendance-page">
      <header className="mb-6">
        <p className="text-xs font-semibold uppercase tracking-wider text-emerald-600 mb-2">Absensi</p>
        <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900">Rekap Absensi Karyawan</h1>
        <p className="mt-2 text-sm text-slate-500">
          Cari &amp; filter absensi per karyawan, cabang, divisi, periode.
          {canEditClock && <> · SPV/Admin/Superadmin bisa <b className="text-violet-700">edit jam</b> via ikon <Pencil className="w-3 h-3 inline text-violet-600" />.</>}
        </p>
      </header>

      {/* Jadwal OFF pada periode yang dipilih (brief owner 29 Agu poin 5,
          diperluas 2 Sep 2026: ikut filter tanggal, bukan lagi selalu hari ini) */}
      {off && (liburUmumTampil.length > 0 || barisOff.length > 0) && (
        <div className="bg-violet-50 border border-violet-200 rounded-xl p-3 mb-4" data-testid="panel-off-hari-ini">
          <p className="text-xs font-semibold text-violet-900">
            OFF / libur {rentangTampil.dari === rentangTampil.sampai
              ? rentangTampil.dari
              : `${rentangTampil.dari} s/d ${rentangTampil.sampai}`}
            {liburUmumTampil.length > 0 && (
              <span className="font-normal"> — libur umum: {liburUmumTampil.map(l => `${l.name} (${l.date})`).join(", ")}</span>
            )}
          </p>
          {barisOff.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-1.5">
              {/* testid memuat tanggal: satu orang bisa OFF beberapa kali dalam
                  satu periode, dan testid kembar membuat pencarian elemen
                  tunggal gagal. */}
              {barisOff.map(b => (
                <span key={b.id} data-testid={`off-${b.employee_id}-${b.date}`}
                      className="text-[11px] px-2 py-0.5 rounded-full bg-white border border-violet-200 text-violet-800">
                  {b.employee_nama}
                  <span className="text-violet-400 font-mono"> · {b.date}</span>
                  {b.keterangan && b.keterangan !== "OFF" && (
                    <span className="text-violet-500"> · {b.keterangan}</span>
                  )}
                </span>
              ))}
            </div>
          )}
          <p className="text-[10px] text-violet-600 mt-1.5">
            Mereka tidak wajib absen, jadi tidak punya jam masuk/pulang — di tabel
            bawah barisnya ditandai <b>OFF</b>.
            {off.terpotong && (
              <span className="text-rose-700 font-semibold"> Daftar dipotong karena
                terlalu panjang — persempit periodenya agar lengkap.</span>
            )}
          </p>
        </div>
      )}

      {/* Filter Panel */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 mb-4 grid grid-cols-2 md:grid-cols-6 gap-3">
        <div className="relative col-span-2">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Cari nama/cabang/divisi..."
            data-testid="input-att-search"
            className="h-10 pl-8 pr-3 rounded-lg border border-slate-200 text-sm w-full" />
        </div>
        <select value={empFilter} onChange={e => setEmpFilter(e.target.value)}
          data-testid="input-att-emp"
          className="h-10 px-2 rounded-lg border border-slate-200 text-sm bg-white">
          <option value="">— Semua Karyawan —</option>
          {emps.map(e => <option key={e.id} value={e.id}>{e.nama}</option>)}
        </select>
        <select value={branchFilter} onChange={e => setBranchFilter(e.target.value)}
          data-testid="input-att-branch"
          className="h-10 px-2 rounded-lg border border-slate-200 text-sm bg-white">
          <option value="">— Semua Cabang —</option>
          {branches.map(b => <option key={b} value={b}>{b}</option>)}
        </select>
        <select value={divisiFilter} onChange={e => setDivisiFilter(e.target.value)}
          data-testid="input-att-divisi"
          className="h-10 px-2 rounded-lg border border-slate-200 text-sm bg-white">
          <option value="">— Semua Divisi —</option>
          {divisis.map(d => <option key={d} value={d}>{d}</option>)}
        </select>
        <select value={sortBy} onChange={e => setSortBy(e.target.value)}
          data-testid="input-att-sort"
          className="h-10 px-2 rounded-lg border border-slate-200 text-sm bg-white">
          <option value="date">Sort: Tanggal (desc)</option>
          <option value="branch">Sort: Cabang</option>
          <option value="divisi">Sort: Divisi</option>
          <option value="nama">Sort: Nama</option>
        </select>
        <div className="col-span-2 md:col-span-3 grid grid-cols-2 gap-2 items-center">
          <label className="text-xs text-slate-500 col-span-2 -mb-1">Periode:</label>
          <DatePicker value={dateFrom} onChange={setDateFrom}
            data-testid="input-att-from" />
          <DatePicker value={dateTo} onChange={setDateTo}
            data-testid="input-att-to" />
        </div>
        <button onClick={clearFilters} data-testid="btn-clear-att-filters"
          className="h-10 rounded-lg border border-slate-200 hover:bg-slate-50 text-sm text-slate-600">
          {/* Dihitung terpisah: baris OFF bukan absensi, menjumlahkannya jadi
              satu angka akan terbaca seolah lebih banyak orang yang hadir. */}
          Reset Filter · {filtered.length} absensi{barisOff.length > 0 ? ` · ${barisOff.length} OFF` : ""}
        </button>
        <button onClick={exportExcel} data-testid="btn-export-att-excel"
          className="col-span-2 md:col-span-2 inline-flex items-center justify-center gap-2 h-10 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium">
          <Download className="w-4 h-4" /> Export Excel Terfilter
        </button>
      </div>

      {/* Cuti Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <SumCard label="Total Cuti" value={cutiSummary.total} tint="slate" icon={Calendar} />
        <SumCard label="Cuti Approved" value={cutiSummary.approved} tint="emerald" icon={CheckCircle2} />
        <SumCard label="Cuti Pending" value={cutiSummary.pending} tint="amber" icon={Clock} />
        <SumCard label="Cuti Ditolak" value={cutiSummary.rejected} tint="rose" icon={XCircle} />
      </div>

      {/* Month vs Month Comparison */}
      <details className="bg-white border border-slate-200 rounded-xl p-4 mb-4">
        <summary className="cursor-pointer text-sm font-semibold text-slate-800 flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-violet-600" /> Perbandingan Bulan vs Bulan
        </summary>
        <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3 items-end">
          <label className="block">
            <span className="text-xs font-medium text-slate-600">Bulan A</span>
            <MonthPicker value={compareMonthA} onChange={setCompareMonthA}
              data-testid="input-compare-a"
              className="mt-1" />
          </label>
          <label className="block">
            <span className="text-xs font-medium text-slate-600">Bulan B</span>
            <MonthPicker value={compareMonthB} onChange={setCompareMonthB}
              data-testid="input-compare-b"
              className="mt-1" />
          </label>
          {(statsA || statsB) && (
            <div className="md:col-span-2 text-xs">
              <table className="w-full">
                <thead className="text-slate-500"><tr>
                  <th className="text-left py-1">Metrik</th>
                  <th className="text-right py-1">{compareMonthA || "-"}</th>
                  <th className="text-right py-1">{compareMonthB || "-"}</th>
                  <th className="text-right py-1">Selisih</th>
                </tr></thead>
                <tbody>
                  <CompareRow label="Total Kehadiran" a={statsA?.total} b={statsB?.total} />
                  <CompareRow label="Total Karyawan Aktif" a={statsA?.uniqueEmps} b={statsB?.uniqueEmps} />
                  <CompareRow label="Jml Telat" a={statsA?.late} b={statsB?.late} lowerIsBetter />
                  <CompareRow label="Menit Telat" a={statsA?.totalLateMinutes} b={statsB?.totalLateMinutes} lowerIsBetter />
                  <CompareRow label="Menit Lembur" a={statsA?.totalOvertime} b={statsB?.totalOvertime} />
                  <CompareRow label="Menit Pulang Awal" a={statsA?.totalEarlyLeave} b={statsB?.totalEarlyLeave} lowerIsBetter />
                  <CompareRow label="Rata-rata Stamina (%)" a={statsA?.avgStamina} b={statsB?.avgStamina} />
                </tbody>
              </table>
            </div>
          )}
        </div>
      </details>

      {/* Chart Kehadiran per Cabang / Divisi */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="chart-branch">
          <p className="text-xs font-semibold text-slate-800 mb-3 inline-flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-emerald-600" /> Kehadiran per Cabang
            <span className="text-[10px] text-slate-400 font-normal">(top 8)</span>
          </p>
          {chartData.byBranch.length === 0 ? (
            <p className="text-xs text-slate-400 italic py-8 text-center">Belum ada data</p>
          ) : (
            <div className="space-y-2">
              {chartData.byBranch.map(([label, val]) => (
                <div key={label} className="flex items-center gap-2 text-xs">
                  <div className="w-24 truncate text-slate-700 font-medium">{label}</div>
                  <div className="flex-1 h-5 rounded bg-slate-100 overflow-hidden relative">
                    <div className="h-full bg-gradient-to-r from-emerald-500 to-emerald-600"
                      style={{ width: `${(val / maxBranch) * 100}%` }} />
                    <span className="absolute inset-0 flex items-center px-2 text-[10px] font-mono font-bold text-slate-800">{val}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="chart-divisi">
          <p className="text-xs font-semibold text-slate-800 mb-3 inline-flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-violet-600" /> Kehadiran per Divisi
            <span className="text-[10px] text-slate-400 font-normal">(top 8)</span>
          </p>
          {chartData.byDivisi.length === 0 ? (
            <p className="text-xs text-slate-400 italic py-8 text-center">Belum ada data</p>
          ) : (
            <div className="space-y-2">
              {chartData.byDivisi.map(([label, val]) => (
                <div key={label} className="flex items-center gap-2 text-xs">
                  <div className="w-24 truncate text-slate-700 font-medium">{label}</div>
                  <div className="flex-1 h-5 rounded bg-slate-100 overflow-hidden relative">
                    <div className="h-full bg-gradient-to-r from-violet-500 to-violet-600"
                      style={{ width: `${(val / maxDivisi) * 100}%` }} />
                    <span className="absolute inset-0 flex items-center px-2 text-[10px] font-mono font-bold text-slate-800">{val}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Analisa AI Stamina — di atas tabel, tabel di bawah tidak diubah */}
      <StaminaAICard />

      {/* Attendance table */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-x-auto">
        <table className="w-full text-sm" data-testid="attendance-table">
          <thead><tr className="bg-slate-50 text-xs font-semibold text-slate-500 uppercase tracking-wider">
            <th className="text-left py-3 px-4">Tanggal</th>
            <th className="text-left py-3 px-4">Nama</th>
            <th className="text-left py-3 px-4">Cabang</th>
            <th className="text-left py-3 px-4">Divisi</th>
            <th className="text-left py-3 px-4">Clock-in</th>
            <th className="text-left py-3 px-4">Clock-out</th>
            <th className="text-right py-3 px-4">Telat</th>
            <th className="text-right py-3 px-4">Lembur</th>
            <th className="text-right py-3 px-4">Pulang Awal</th>
            <th className="text-right py-3 px-4">Stamina</th>
            {canEditClock && <th className="text-right py-3 px-4"></th>}
          </tr></thead>
          <tbody>
            {barisTampil.map(a => a._off ? (
              /* Baris OFF: bukan absensi, jadi tidak punya jam apa pun dan
                 sengaja TIDAK diberi tombol edit jam — tidak ada dokumen
                 absensi untuk diedit. Ditandai jelas supaya tidak terbaca
                 sebagai "hadir tapi datanya kosong". */
              <tr key={a.id} className="border-b border-slate-100 bg-violet-50/40"
                  data-testid={`att-off-row-${a.employee_id}-${a.date}`}>
                <td className="py-3 px-4 font-mono text-xs">{a.date}</td>
                <td className="py-3 px-4 font-medium text-slate-900">
                  {a.employee_nama}
                  <span className="ml-1.5 text-[9px] px-1.5 py-0.5 rounded-full bg-violet-200 text-violet-900 font-bold uppercase">OFF</span>
                </td>
                <td className="py-3 px-4 text-slate-600 text-xs">{a.branch || "-"}</td>
                <td className="py-3 px-4 text-slate-600 text-xs">{a.divisi || "-"}</td>
                <td className="py-3 px-4 text-xs text-violet-700" colSpan={canEditClock ? 7 : 6}>
                  Libur terjadwal{a.keterangan && a.keterangan !== "OFF" ? ` — ${a.keterangan}` : ""} · tidak wajib absen
                </td>
              </tr>
            ) : (
              <tr key={a.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`att-row-${a.id}`}>
                <td className="py-3 px-4 font-mono text-xs">{a.date}</td>
                <td className="py-3 px-4 font-medium text-slate-900">
                  {a.employee_nama}
                  {a.edited_by && <span className="ml-1 text-[9px] px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-800 font-bold uppercase" title={`Bypass: ${a.edit_note || "-"}`}>Bypass</span>}
                </td>
                <td className="py-3 px-4 text-slate-600 text-xs">{a.branch || "-"}</td>
                <td className="py-3 px-4 text-slate-600 text-xs">{a.divisi || "-"}</td>
                <td className="py-3 px-4 font-mono text-xs">{fmt(a.clock_in)}</td>
                <td className="py-3 px-4 font-mono text-xs">{fmt(a.clock_out)}</td>
                <td className="py-3 px-4 text-right font-semibold text-xs">
                  {a.late_minutes > 0 ? <span className="text-rose-700">{a.late_minutes}m</span> : <span className="text-emerald-700">0</span>}
                </td>
                <td className="py-3 px-4 text-right font-mono text-xs text-slate-600">
                  {a.overtime_minutes > 0 ? `${a.overtime_minutes}m` : "-"}
                </td>
                <td className="py-3 px-4 text-right font-mono text-xs" data-testid={`att-early-${a.id}`}>
                  {a.counted_absent
                    ? <span className="text-rose-700 font-semibold" title={`Jam kerja ${a.work_pct}% dari shift — di bawah ambang, dihitung tidak hadir`}>
                        &lt;ambang
                      </span>
                    : a.early_leave_minutes > 0
                      ? <span className="text-amber-700">{a.early_leave_minutes}m</span>
                      : <span className="text-slate-400">-</span>}
                </td>
                <td className="py-3 px-4 text-right" data-testid={`att-stamina-${a.id}`}>
                  {a.stamina_pct == null ? (
                    <span className="text-xs text-slate-400">-</span>
                  ) : (
                    <span title={a.stamina_note || ""}
                      className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${
                        a.stamina_pct >= 80 ? "bg-emerald-100 text-emerald-800" :
                        a.stamina_pct >= 60 ? "bg-sky-100 text-sky-800" :
                        a.stamina_pct >= 40 ? "bg-amber-100 text-amber-800" :
                        "bg-rose-100 text-rose-800"
                      }`}>
                      {a.stamina_pct}%
                    </span>
                  )}
                </td>
                {canEditClock && (
                  <td className="py-3 px-4 text-right">
                    <button onClick={() => openEditClock(a)} data-testid={`btn-edit-att-${a.id}`}
                      className="p-2 rounded-lg hover:bg-violet-50 text-slate-400 hover:text-violet-700">
                      <Pencil className="w-4 h-4" />
                    </button>
                  </td>
                )}
              </tr>
            ))}
            {barisTampil.length === 0 && <tr><td colSpan={canEditClock ? 11 : 10} className="text-center py-12 text-sm text-slate-500">
              <Clock className="w-8 h-8 text-slate-300 mx-auto mb-2" />Tidak ada data absensi cocok filter
            </td></tr>}
          </tbody>
        </table>
      </div>

      {/* Edit Clock Modal */}
      {editModal && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm grid place-items-center p-4" onClick={() => setEditModal(null)}>
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-violet-600 to-fuchsia-500 text-white px-5 py-4 rounded-t-2xl">
              <h3 className="font-display font-semibold text-sm">Edit Jam Absensi (Bypass)</h3>
              <p className="text-[11px] text-violet-100">{editModal.att.employee_nama} · {editModal.att.date}</p>
            </div>
            <div className="p-5 space-y-3">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-2 text-[11px] text-amber-800">
                <AlertTriangle className="w-3 h-3 inline mr-1" />Perubahan tercatat di audit log &amp; notif ke karyawan. Format HH:MM (WIB).
              </div>
              <div className="grid grid-cols-2 gap-3">
                <label className="block"><span className="text-xs font-medium text-slate-600">Clock-In</span>
                  <input type="time" value={editForm.clock_in} onChange={e => setEditForm(s => ({ ...s, clock_in: e.target.value }))}
                    data-testid="edit-clock-in" className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm font-mono" />
                </label>
                <label className="block"><span className="text-xs font-medium text-slate-600">Clock-Out</span>
                  <input type="time" value={editForm.clock_out} onChange={e => setEditForm(s => ({ ...s, clock_out: e.target.value }))}
                    data-testid="edit-clock-out" className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm font-mono" />
                </label>
              </div>
              <label className="block"><span className="text-xs font-medium text-slate-600">Catatan Alasan *</span>
                <textarea value={editForm.note} onChange={e => setEditForm(s => ({ ...s, note: e.target.value }))} rows={2}
                  data-testid="edit-clock-note"
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm resize-none" />
              </label>
              <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                <button onClick={() => setEditModal(null)} className="h-10 px-4 rounded-lg border border-slate-200 text-sm">Batal</button>
                <button onClick={saveEditClock} disabled={editSaving} data-testid="btn-save-edit-clock"
                  className="inline-flex items-center gap-1.5 h-10 px-5 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-sm font-medium disabled:opacity-50">
                  <Save className="w-4 h-4" /> {editSaving ? "..." : "Simpan"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ================= Analisa AI Stamina ================= */
function StaminaAICard() {
  // Gerbang fitur AI (brief owner 30 Agu 2026) — kunci `ai_assistant`, SAMA
  // dengan gerbang backend di `/ai/stamina-analysis`. Ditaruh di dalam kartu
  // (bukan di pemanggilnya) supaya aturannya menempel pada komponennya.
  const { user, can } = useAuth();
  const [days, setDays] = useState(7);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  const analisa = async (refresh = false) => {
    setLoading(true); setError("");
    try {
      const { data: d } = await api.get(`/ai/stamina-analysis?days=${days}${refresh ? "&refresh=true" : ""}`);
      setData(d);
    } catch (e) {
      // 503 = AI belum dikonfigurasi — bukan bug. Alasannya ditampilkan tenang
      // di dalam kartu, bukan toast merah yang bikin panik tiap buka halaman.
      setData(null);
      setError(e?.response?.data?.detail || "Gagal memuat analisa AI");
    } finally { setLoading(false); }
  };

  // Sesudah semua hook (Rules of Hooks) — kartunya hilang total, bukan tombol
  // yang terlihat lalu balas 403.
  if (!bolehPakaiAI(user, can)) return null;

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 mb-4" data-testid="stamina-ai-card">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <p className="text-sm font-semibold text-slate-800 inline-flex items-center gap-2 mr-auto">
          <Sparkles className="w-4 h-4 text-emerald-600" /> Analisa AI Stamina
          <span className="text-[10px] text-slate-400 font-normal">rata-rata hanya dari hari yang diisi — kosong ≠ nol</span>
        </p>
        <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden">
          {[7, 30].map(d => (
            <button key={d} onClick={() => setDays(d)} disabled={loading}
              data-testid={`btn-stamina-days-${d}`}
              className={`h-9 px-3 text-xs font-medium transition-colors ${
                days === d ? "bg-emerald-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
              {d} hari
            </button>
          ))}
        </div>
        <button onClick={() => analisa(false)} disabled={loading} data-testid="btn-stamina-analisa"
          className="h-9 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium inline-flex items-center gap-1.5 disabled:opacity-60">
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
          {loading ? "Menganalisa…" : "Analisa"}
        </button>
      </div>

      {error && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900 inline-flex items-start gap-2 w-full"
          data-testid="stamina-ai-error">
          <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {!error && !data && !loading && (
        <p className="text-xs text-slate-400 italic">
          Pilih rentang lalu klik &quot;Analisa&quot; — AI merangkum stamina, keterlambatan, dan jam kerja tim dari data absensi.
        </p>
      )}

      {data?.kosong && (
        <p className="text-xs text-slate-500 italic" data-testid="stamina-ai-kosong">
          Belum ada data absensi dalam {data.days} hari terakhir — tidak ada yang bisa dianalisa.
        </p>
      )}

      {data && !data.kosong && (
        <div className="space-y-3" data-testid="stamina-ai-hasil">
          <p className="text-sm text-slate-700 leading-relaxed">{data.ringkasan}</p>

          {(data.sorotan || []).length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {data.sorotan.map((s, i) => (
                <div key={i} className="rounded-lg border border-slate-200 bg-slate-50/60 p-3">
                  <p className="text-xs font-semibold text-slate-900">{s.nama}</p>
                  <p className="text-xs text-slate-600 mt-0.5">{s.temuan}</p>
                  {s.saran && <p className="text-xs text-emerald-700 mt-1">Saran: {s.saran}</p>}
                </div>
              ))}
            </div>
          )}

          {data.pola && (
            <div className="rounded-lg border border-violet-200 bg-violet-50/60 p-3 text-xs text-violet-900">
              <span className="font-semibold">Pola: </span>{data.pola}
            </div>
          )}

          <div className="flex flex-wrap items-center gap-2 text-[10px] text-slate-400 border-t border-slate-100 pt-2">
            <span>
              Periode {data.periode_mulai} – {data.periode_akhir} · {data.jumlah_karyawan} karyawan dianalisa
              {data.cached ? " · dari cache hari ini" : ""}
            </span>
            <button onClick={() => analisa(true)} disabled={loading} data-testid="btn-stamina-refresh"
              className="ml-auto inline-flex items-center gap-1 text-slate-500 hover:text-emerald-700 disabled:opacity-50">
              <RefreshCw className="w-3 h-3" /> Analisa ulang (lewati cache)
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function SumCard({ label, value, tint, icon: Icon }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4">
      <div className={`w-9 h-9 rounded-lg grid place-items-center bg-${tint}-50 text-${tint}-600 mb-2`}>
        <Icon className="w-4 h-4" />
      </div>
      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">{label}</p>
      <p className="font-display text-2xl font-bold text-slate-900">{value}</p>
    </div>
  );
}

function CompareRow({ label, a, b, lowerIsBetter }) {
  const A = a || 0; const B = b || 0;
  const diff = B - A;
  const better = lowerIsBetter ? diff < 0 : diff > 0;
  return (
    <tr className="border-t border-slate-100">
      <td className="py-1.5 text-slate-700">{label}</td>
      <td className="text-right font-mono">{A}</td>
      <td className="text-right font-mono">{B}</td>
      <td className={`text-right font-mono font-semibold ${diff === 0 ? "text-slate-400" : better ? "text-emerald-700" : "text-rose-700"}`}>
        {diff > 0 ? `+${diff}` : diff}
      </td>
    </tr>
  );
}
